/**
 * @description
 * Stripe customer service for managing customer operations and payment methods.
 *
 * @author AI Developer <ai@byteable.ai>
 * @date January 8, 2025
 */

import { BaseAPIClient } from "../core/base-client";
import type { StripeCustomer, StripePaymentMethod } from "./types";

/**
 * Stripe customer service
 */
export class StripeCustomerService extends BaseAPIClient {
  /**
   * Get Stripe customer for client
   */
  async getStripeCustomer(clientId: string): Promise<StripeCustomer> {
    const data = await this.request<{ customer: StripeCustomer }>(`/stripe/customers/client/${clientId}`);
    return data.customer;
  }

  /**
   * Create Stripe customer for client
   */
  async createStripeCustomer(clientId: string, data: { email: string; name: string }): Promise<StripeCustomer> {
    const response = await this.request<{ customer: StripeCustomer }>('/stripe/customers', {
      method: 'POST',
      body: JSON.stringify({ clientId, ...data }),
    });
    return response.customer;
  }

  /**
   * Link existing Stripe customer to client
   */
  async linkStripeCustomer(clientId: string, stripeCustomerId: string): Promise<void> {
    await this.request(`/stripe/customers/client/${clientId}`, {
      method: 'PUT',
      body: JSON.stringify({ stripeCustomerId }),
    });
  }

  /**
   * Get customer portal URL
   */
  async getCustomerPortalUrl(customerId: string, returnUrl: string): Promise<{ url: string }> {
    return this.request<{ url: string }>(`/stripe/payment-portal/${customerId}?return_url=${encodeURIComponent(returnUrl)}`);
  }

  /**
   * Get payment methods for a Stripe customer
   */
  async getPaymentMethods(customerId: string): Promise<StripePaymentMethod[]> {
    const response = await this.request<{ paymentMethods: StripePaymentMethod[] }>(`/stripe/payment-methods/${customerId}`);
    return response.paymentMethods;
  }
}

// Export singleton instance
export const stripeCustomerService = new StripeCustomerService();
